/**
 * Market API Service
 *
 * Fetches market-wide indicators from backend:
 * - Fear & Greed Index
 * - Volatility metrics
 * - Momentum indicators
 */

import { logger } from './logger';
import { fetchWithRetry, isNetworkError } from '../utils/retry';
import { marketFallback } from './marketFallback';

// Use Vercel serverless endpoints (same domain)
const API_BASE_URL = import.meta.env.VITE_ADVICE_BOT_API_URL || '';

export interface FearGreedIndex {
  value: number;
  classification: string;
  timestamp: number;
  timeUntilUpdate: number;
}

export interface VolatilityMetrics {
  symbol: string;
  volatilityIndex: number;
  metrics: {
    atr: number;
    atrPercent: number;
    bollingerBandWidth: number;
  };
  classification: string;
}

export interface MomentumIndicators {
  symbol: string;
  momentumIndex: number;
  metrics: {
    rsi: number;
    macd: {
      value: number;
      signal: number;
      histogram: number;
    } | null;
  };
  classification: string;
}

export class MarketAPI {
  /**
   * Get current Fear & Greed Index
   */
  static async getFearGreedIndex(): Promise<FearGreedIndex | null> {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/api/advice/market?type=fear-greed`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        },
        {
          maxAttempts: 3,
          baseDelay: 1000,
          onRetry: (attempt) => {
            logger.info('MarketAPI', `Retrying Fear & Greed Index fetch (attempt ${attempt})`);
          }
        }
      );

      if (!response.ok) {
        logger.warn('MarketAPI', 'Failed to fetch Fear & Greed Index', { status: response.status, statusText: response.statusText });
        logger.updateServiceStatus('Fear & Greed API', 'unavailable', response.statusText);
        // Return fallback on failure
        return marketFallback.getFearGreedFallback();
      }

      const data = await response.json();
      logger.updateServiceStatus('Fear & Greed API', 'available');

      if (data.success && data.data) {
        // Cache successful response
        marketFallback.cacheData('fear-greed', data.data);
        return data.data;
      }

      return marketFallback.getFearGreedFallback();
    } catch (error) {
      logger.error('MarketAPI', 'Error fetching Fear & Greed Index', error);
      logger.updateServiceStatus('Fear & Greed API', 'unavailable', String(error));
      // Return fallback on error
      return marketFallback.getFearGreedFallback();
    }
  }

  /**
   * Get volatility metrics for a symbol
   */
  static async getVolatilityMetrics(symbol: string): Promise<VolatilityMetrics | null> {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/api/advice/market?type=volatility&symbol=${symbol}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        },
        {
          maxAttempts: 3,
          baseDelay: 1000
        }
      );

      if (!response.ok) {
        logger.warn('MarketAPI', `Failed to fetch volatility for ${symbol}`, { status: response.status });
        logger.updateServiceStatus('Volatility API', 'unavailable', response.statusText);
        // Return fallback on failure
        return marketFallback.getVolatilityFallback(symbol);
      }

      const data = await response.json();
      logger.updateServiceStatus('Volatility API', 'available');

      if (data.success && data.data) {
        // Cache successful response
        marketFallback.cacheData(`volatility-${symbol}`, data.data);
        return data.data;
      }

      return marketFallback.getVolatilityFallback(symbol);
    } catch (error) {
      logger.error('MarketAPI', `Error fetching volatility for ${symbol}`, error);
      logger.updateServiceStatus('Volatility API', 'unavailable', String(error));
      // Return fallback on error
      return marketFallback.getVolatilityFallback(symbol);
    }
  }

  /**
   * Get momentum indicators for a symbol
   */
  static async getMomentumIndicators(symbol: string): Promise<MomentumIndicators | null> {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/api/advice/market?type=momentum&symbol=${symbol}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        },
        {
          maxAttempts: 3,
          baseDelay: 1000
        }
      );

      if (!response.ok) {
        logger.warn('MarketAPI', `Failed to fetch momentum for ${symbol}`, { status: response.status });
        logger.updateServiceStatus('Momentum API', 'unavailable', response.statusText);
        // Return fallback on failure
        return marketFallback.getMomentumFallback(symbol);
      }

      const data = await response.json();
      logger.updateServiceStatus('Momentum API', 'available');

      if (data.success && data.data) {
        // Cache successful response
        marketFallback.cacheData(`momentum-${symbol}`, data.data);
        return data.data;
      }

      return marketFallback.getMomentumFallback(symbol);
    } catch (error) {
      logger.error('MarketAPI', `Error fetching momentum for ${symbol}`, error);
      logger.updateServiceStatus('Momentum API', 'unavailable', String(error));
      // Return fallback on error
      return marketFallback.getMomentumFallback(symbol);
    }
  }

  /**
   * Check if backend is available
   */
  static async healthCheck(): Promise<boolean> {
    try {
      const response = await fetchWithRetry(
        `${BACKEND_URL}/health`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        },
        {
          maxAttempts: 2, // Fewer retries for health check
          baseDelay: 500
        }
      );

      const isHealthy = response.ok;
      logger.updateServiceStatus('Backend Health', isHealthy ? 'available' : 'unavailable');
      return isHealthy;
    } catch (error) {
      logger.error('MarketAPI', 'Backend health check failed', error);
      logger.updateServiceStatus('Backend Health', 'unavailable', String(error));
      return false;
    }
  }
}
